import type { RefObject } from "react";
import { Button, Flex, Paper } from "@mantine/core";
import "./BusinessInfoSetting.scss";
import type { BusinessInfoSettings } from "../../types";
import {
  useNotification,
  useColorValidation,
  useLinkValidation,
  useSlugValidation,
  NotificationDisplay,
  BusinessBrandingSection,
  BusinessInfoFields,
} from "./components";
import SettingsHeader from "../Settings/SettingsHeader/SettingsHeader";

interface BusinessInfoSettingProps {
  businessInfo: BusinessInfoSettings;
  setBusinessInfo: (businessInfo: BusinessInfoSettings) => void;
  updateBusinessInfo: (businessInfo: BusinessInfoSettings) => any;
  children?: JSX.Element;
  isSettingsPage?: boolean;
  formRef?: RefObject<HTMLFormElement>;
  minimize?: boolean;
}

/**
 * Function for managing business information settings.
 *
 * @return {JSX.Element} The form for managing business information settings.
 */
const BusinessInfoSetting = ({
  businessInfo,
  setBusinessInfo,
  updateBusinessInfo,
  children,
  isSettingsPage = true,
  formRef,
  minimize = false,
}: BusinessInfoSettingProps) => {
  const { notification, showNotification, clearNotification } =
    useNotification();
  const { isColorValid, validateColor } = useColorValidation();
  const {
    calendlyLinkError,
    paymentLinkError,
    validateCalendlyLink,
    validatePaymentLink,
  } = useLinkValidation();
  const {
    vanitySlugError,
    slugAvailability,
    validateVanitySlug,
    setSlugAvailability,
  } = useSlugValidation(businessInfo.vanitySlug);

  /**
   * checks if response status is 201 and updates business informations
   * @param e event
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isColorValid) {
      showNotification(
        "Error",
        "Please select a darker primary color before saving",
        "red"
      );
      return;
    }

    showNotification(
      "Updating Business Info",
      "Updating business info...",
      "blue"
    );

    try {
      const currentBusinessInfo = JSON.parse(
        localStorage.getItem("businessInfo") as string
      );

      const payload = {
        businessName: businessInfo.businessName,
        businessWebsite: businessInfo.businessWebsite,
        businessEmail: businessInfo.businessEmail,
        businessPhone: businessInfo.businessPhone,
        consultLink: businessInfo.consultLink,
        paymentLink: businessInfo.paymentLink,
        logoKey: businessInfo.logoKey,
        primaryColor: businessInfo.primaryColor,
        loginMessage: businessInfo.loginMessage,
        vanitySlug: businessInfo.vanitySlug,
      };

      const updatedBusinessInfo = {
        ...currentBusinessInfo,
        ...payload,
      };

      const response = await updateBusinessInfo(payload);

      localStorage.setItem("businessInfo", JSON.stringify(updatedBusinessInfo));

      if (response?.status === 200) {
        setSlugAvailability(null);
        showNotification(
          "Success",
          "Business info updated successfully!",
          "teal"
        );
      } else {
        showNotification("Error", "Business info updated unsuccessful!", "red");
      }
    } catch (error) {
      console.error(error);
      showNotification("Error", "Failed to update business information", "red");
    }
  };

  return (
    <Paper className="business-info" p="xl" radius="xl">
      <form ref={formRef} onSubmit={handleSubmit}>
        <NotificationDisplay
          notification={notification}
          onClose={clearNotification}
        />

        <div className="business-form-header">
          {isSettingsPage && (
            <Flex justify="space-between" align="center" w="100%" mb="lg">
              <SettingsHeader title="Business Information" order={3} />
              <Button
                variant="outline"
                color="blue"
                type="submit"
                disabled={notification?.message === "Updating business info..."}
                data-cy="saveBusinessInfoButton"
              >
                Save
              </Button>
            </Flex>
          )}
        </div>

        <BusinessBrandingSection
          businessInfo={businessInfo}
          setBusinessInfo={setBusinessInfo}
          children={children}
          isColorValid={isColorValid}
          validateColor={validateColor}
          vanitySlugError={vanitySlugError}
          slugAvailability={slugAvailability}
          validateVanitySlug={validateVanitySlug}
        />

        <BusinessInfoFields
          businessInfo={businessInfo}
          setBusinessInfo={setBusinessInfo}
          minimize={minimize}
          calendlyLinkError={calendlyLinkError}
          paymentLinkError={paymentLinkError}
          validateCalendlyLink={validateCalendlyLink}
          validatePaymentLink={validatePaymentLink}
        />
      </form>
    </Paper>
  );
};

export default BusinessInfoSetting;
